import compact from 'lodash/compact'
import includes from 'lodash/includes'
import kebabCase from 'lodash/kebabCase'
import map from 'lodash/map'
import dynamic from 'next/dynamic'
import { useRouter } from 'next/router'
import React, { ReactNode, useMemo } from 'react'

import SplitLine from '@Components/CMS/SplitLine'
import Trustpilot from '@Components/CMS/Trustpilot'
import {
  Block,
  BlockTypeWithGlobal,
  GalleryBlock as GalleryBlockData,
  GlobalBlock,
  PageGridBlock as PageGridBlockData,
  ProductGridSelectorBlock as ProductGridSelectorBlockData,
  ProductListBlock as ProductListBlockData,
  SplitTestGroup,
} from '@Generated/graphql'
import { BlockContext } from '@Globals/CMS/Context'
import { Experiment } from '@Globals/SplitTests/Experiment'

interface BlockOptions {
  id: string
  idPrefix: string
  ids: Record<string, number>
  index: number
  locale: string
}

type BlockToComponentMap = {
  [key: string]: (block: BlockTypeWithGlobal, options: BlockOptions) => ReactNode
}

const getBlockID = (block: BlockTypeWithGlobal) => `${kebabCase(block.type || '').replace('-block', '')}-${block.id}`

const AccordionListBlock = dynamic(() => import('@Components/CMS/Blocks/AccordionListBlock'))
const BuyButtonBlock = dynamic(() => import('@Components/CMS/Blocks/BuyButtonBlock'))
const CategoryListBlock = dynamic(() => import('@Components/CMS/Blocks/CategoryListBlock'))
const EmbedCodeBlock = dynamic(() => import('@Components/CMS/Blocks/EmbedCodeBlock'))
const FactBlock = dynamic(() => import('@Components/CMS/Blocks/FactBlock'))
const GalleryBlock = dynamic(() => import('@Components/CMS/Blocks/GalleryBlock'))
const ImageBlock = dynamic(() => import('@Components/CMS/Blocks/ImageBlock'))
const ImageGridBlock = dynamic(() => import('@Components/CMS/Blocks/ImageGridBlock'))
const LeadMagnetBlock = dynamic(() => import('@Components/CMS/Blocks/LeadMagnetBlock'))
const LinkBlock = dynamic(() => import('@Components/CMS/Blocks/LinkBlock'))
const MediaGalleryBlock = dynamic(() => import('@Components/CMS/Blocks/MediaGalleryBlock'))
const NotifyMeBlock = dynamic(() => import('@Components/CMS/Blocks/NotifyMeBlock'))
const PageGridBlock = dynamic(() => import('@Components/CMS/Blocks/PageGridBlock'))
const PreviouslySeenProductsBlock = dynamic(() => import('@Components/CMS/Blocks/PreviouslySeenProductsBlock'))
const ProductAndImageGridBlock = dynamic(() => import('@Components/CMS/Blocks/ProductAndImageGridBlock'))
const ProductDisplayBlock = dynamic(() => import('@Components/CMS/Blocks/ProductDisplayBlock'))
const ProductGridSelectorBlock = dynamic(() => import('@Components/CMS/Blocks/ProductGridSelectorBlock'))
const ProductListBlock = dynamic(() => import('@Components/CMS/Blocks/ProductListBlock'))
const ProductRecommendationsBlock = dynamic(() => import('@Components/CMS/Blocks/ProductRecommendationsBlock'))
const QuoteBlock = dynamic(() => import('@Components/CMS/Blocks/QuoteBlock'))
const ShippingBlock = dynamic(() => import('@Components/CMS/Blocks/ShippingBlock'))
const ShippingMatrixBlock = dynamic(() => import('@Components/CMS/Blocks/ShippingMatrixBlock'))
const SubscribeBlock = dynamic(() => import('@Components/CMS/Blocks/SubscribeBlock'))
const TextBlock = dynamic(() => import('@Components/CMS/Blocks/TextBlock'))
const TitleBlock = dynamic(() => import('@Components/CMS/Blocks/TitleBlock'))
const UrgencyTimerBlock = dynamic(() => import('@Components/CMS/Blocks/UrgencyTimerBlock'))
const VideoBlock = dynamic(() => import('@Components/CMS/Blocks/VideoBlock'))

const blockToComponentMap: BlockToComponentMap = {
  accordionListBlock: (block: Block, { id }) => <AccordionListBlock key={id} accordionListBlock={block} id={id} />,
  buttonLinkBlock: (block: Block, { id }) => <LinkBlock key={id} id={id} linkBlock={block} />,
  buyButtonBlock: (block: Block, { id }) => <BuyButtonBlock key={id} buyButtonBlock={block} id={id} />,
  categoryListBlock: (block: Block, { id }) => <CategoryListBlock key={id} categoryListBlock={block} id={id} />,
  embedCodeBlock: (block: Block, { id }) => <EmbedCodeBlock key={id} embedCodeBlock={block} id={id} />,
  factBlock: (block: Block, { id }) => <FactBlock key={id} factBlock={block} id={id} />,
  galleryBlock: (block: GalleryBlockData, { id }) => <GalleryBlock key={id} galleryBlock={block} id={id} />,
  // TODO: Do we need to support linking to the top of a global block?
  // eslint-disable-next-line @typescript-eslint/no-use-before-define
  globalBlock: (block: GlobalBlock, { idPrefix, ids, locale }) => getComponentsForBlocks(block.blocks || [], locale, idPrefix, ids),
  imageBlock: (block: Block, { id, index }) => <ImageBlock key={id} id={id} imageBlock={block} index={index} />,
  imageGridBlock: (block: Block, { id }) => <ImageGridBlock key={id} id={id} imageGridBlock={block} />,
  // TODO: Do we need to support linking to a lead magnet block, and if so where would it scroll to?
  leadMagnetBlock: (block: Block, { id }) => <LeadMagnetBlock key={id} leadMagnetBlock={block} />,
  linkBlock: (block: Block, { id }) => <LinkBlock key={id} id={id} linkBlock={block} />,
  mediaGalleryBlock: (block: Block, { id }) => <MediaGalleryBlock key={id} id={id} mediaGalleryBlock={block} />,
  notifyMeBlock: (block: Block, { id }) => <NotifyMeBlock key={id} id={id} notifyMeBlock={block} />,
  pageGridBlock: (block: PageGridBlockData, { id }) => <PageGridBlock key={id} id={id} pageGridBlock={block} />,
  previousSeenProductsBlock: (block: Block, { id }) => <PreviouslySeenProductsBlock key={id} id={id} />,
  productDisplayBlock: (_, { id }) => <ProductDisplayBlock key={id} />,
  productAndImageGridBlock: (block: Block, { id }) => <ProductAndImageGridBlock key={id} id={id} productAndImageGridBlock={block} />,
  productGridSelectorBlock: (block: ProductGridSelectorBlockData, { id }) => <ProductGridSelectorBlock key={id} id={id} productGridSelectorBlock={block} />,
  productRecommendationsBlock: (block: Block, { id }) => <ProductRecommendationsBlock key={id} id={id} productRecommendationsBlock={block} />,
  quoteBlock: (block: Block, { id }) => <QuoteBlock key={id} id={id} quoteBlock={block} />,
  splitLineBlock: (block: Block, { id }) => <SplitLine key={id} id={id} />,
  textBlock: (block: Block, { id }) => <TextBlock key={id} id={id} textBlock={block} />,
  titleBlock: (block: Block, { id }) => <TitleBlock key={id} id={id} titleBlock={block} />,
  trustpilotBlock: (block: Block, { id }) => <Trustpilot key={id} id={id} />,
  urgencyTimerBlock: (block: Block, { id }) => <UrgencyTimerBlock key={id} id={id} urgencyTimerBlock={block} />,
  videoBlock: (block: Block, { id, index }) => <VideoBlock key={id} blockIndex={index} id={id} videoBlock={block} />,
  shippingUrgencyTimerBlock: (block: Block, { id }) => <ShippingBlock key={id} id={id} shippingBlock={block} />,
  shippingMatrixBlock: (block: Block, { id }) => <ShippingMatrixBlock key={id} id={id} shippingMatrixBlock={block} />,
  subscribeBlock: (block: Block, { id }) => <SubscribeBlock key={id} id={id} subscribeBlock={block} />,
  productListBlock: (block: ProductListBlockData, { id }) => <ProductListBlock key={id} id={id} productListBlock={block} />,
}

const getComponentsForBlocks = (
  blocks: BlockTypeWithGlobal[],
  locale: string,
  idPrefix: string,
  ids: Record<string, number> = {},
): ReactNode[] => compact(
  map(
    blocks,
    (block, index) => {
      let id = getBlockID(block)
      // eslint-disable-next-line no-param-reassign
      ids[id] = (ids[id] || 0) + 1

      if (ids[id] > 1) {
        id = `${id}-${ids[id]}`
      }

      if (idPrefix) {
        id = `${idPrefix}-${id}`
      }

      let blockComponent = blockToComponentMap[block.type || '']?.(block, { id, idPrefix, ids, index, locale })
      const { splitTest } = block

      if (splitTest?.test) {
        /* eslint-disable react/jsx-no-useless-fragment */
        blockComponent = (
          <Experiment
            A={includes(splitTest.groups, SplitTestGroup.A) ? <>{blockComponent}</> : undefined}
            B={includes(splitTest.groups, SplitTestGroup.B) ? <>{blockComponent}</> : undefined}
            C={includes(splitTest.groups, SplitTestGroup.C) ? <>{blockComponent}</> : undefined}
            D={includes(splitTest.groups, SplitTestGroup.D) ? <>{blockComponent}</> : undefined}
            test={splitTest.test}
          />
        )
        /* eslint-enable react/jsx-no-useless-fragment */
      }

      return (
        // eslint-disable-next-line react/jsx-no-constructed-context-values
        <BlockContext.Provider key={id} value={{ blockID: block.id || '', blockType: block.type || '', id }}>
          {blockComponent}
        </BlockContext.Provider>
      )
    },
  ),
)

export const useBlockComponents = (blocks: BlockTypeWithGlobal[], idPrefix: string = ''): ReactNode[] => {
  const { locale = '' } = useRouter()
  return useMemo(() => getComponentsForBlocks(blocks, locale, idPrefix), [blocks, idPrefix, locale])
}
